import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';

const UniversalHollywoodPackageForm = ({ onSave, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    title: 'Universal Studios Hollywood Package',
    hotel_name: '',
    custom_hotel_name: '',
    room_type: '',
    view: '',
    check_in_date: '',
    check_out_date: '',
    ticket_type: '1-Day General Admission',
    express_pass: 'None',
    vip_experience: 'None',
    studio_tour: 'Regular Studio Tour',
    behind_scenes_tours: '',
    ground_transportation: '',
    travel_protection: 'Not Included',
    travel_protection_price: 0,
    total_price: 0,
    total_price_due_by: '',
    deposit_due: 0,
    deposit_due_by: '',
    special_requests: '',
    children: [],
    isOption: false,
    optionDescription: '',
    photos: [],
    documents: [],
    ...initialData
  });

  const [errors, setErrors] = useState({});

  // Universal Studios Hollywood Partner Hotels
  const hotelOptions = [
    // Nearby Hotels
    "Hilton Los Angeles/Universal City",
    "Sheraton Universal Hotel",
    "The Garland",
    "Hotel Amarano Burbank",
    "Marriott Burbank Airport",
    "Holiday Inn Burbank-Media Center",
    "Best Western Plus Media Center Inn & Suites",
    "Hampton Inn & Suites Los Angeles/Sherman Oaks",
    "Courtyard by Marriott Los Angeles Burbank Airport",
    "Residence Inn by Marriott Los Angeles Burbank/Downtown",
    "Extended Stay America Suites - Los Angeles - Burbank Airport",
    "Tangerine Hotel Burbank",
    "Safari Inn",
    "Coast Anabelle Hotel",
    "Hotel Burbank"
  ];

  const ticketTypes = [
    '1-Day General Admission',
    '2-Day General Admission',
    '3-Day General Admission',
    '4-Day General Admission',
    '5-Day General Admission',
    '6-Day General Admission',
    '7-Day General Admission'
  ];

  const expressPassOptions = [
    'None',
    'Universal Express',
    'Front of Line Pass'
  ];

  const vipExperienceOptions = [
    'None',
    'VIP Experience',
    'Behind-the-Scenes VIP Experience'
  ];

  const studioTourOptions = [
    'Regular Studio Tour',
    'VIP Studio Tour'
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? parseFloat(value) || 0 : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      // Clear custom hotel name if hotel selection changes to something other than "Other"
      if (name === 'hotel_name' && newValue !== 'Other') {
        updated.custom_hotel_name = '';
      }
      
      return updated;
    });
  };

  const handleChildrenChange = (count) => {
    const children = Array.from({ length: count }, (_, i) => 
      formData.children[i] || { age: 5 }
    );
    setFormData(prev => ({ ...prev, children }));
  };

  const handleChildAgeChange = (index, age) => {
    const updatedChildren = [...formData.children];
    updatedChildren[index] = { age: parseInt(age) };
    setFormData(prev => ({ ...prev, children: updatedChildren }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.check_in_date) newErrors.check_in_date = 'Check-in date is required';
    if (!formData.check_out_date) newErrors.check_out_date = 'Check-out date is required';
    if (!formData.hotel_name) newErrors.hotel_name = 'Hotel selection is required';
    if (formData.hotel_name === 'Other' && !formData.custom_hotel_name) {
      newErrors.custom_hotel_name = 'Custom hotel name is required when Other is selected';
    }
    if (!formData.total_price) newErrors.total_price = 'Total price is required';
    if (!formData.deposit_due) newErrors.deposit_due = 'Deposit amount is required';

    // Date validation
    if (formData.check_in_date && formData.check_out_date) {
      const checkIn = new Date(formData.check_in_date);
      const checkOut = new Date(formData.check_out_date);
      if (checkOut <= checkIn) {
        newErrors.check_out_date = 'Check-out date must be after check-in date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Universal Hollywood form submitted with data:', formData);
    
    if (validateForm()) {
      const componentData = {
        type: 'universal-hollywood-package',
        title: formData.title,
        price: formData.total_price,
        isOption: formData.isOption,
        optionDescription: formData.optionDescription,
        paymentTiming: formData.paymentTiming,
        optionalDepositAmount: formData.optionalDepositAmount,
        photos: formData.photos,
        documents: formData.documents,
        data: {
          hotel_name: formData.hotel_name,
          custom_hotel_name: formData.custom_hotel_name,
          room_type: formData.room_type,
          view: formData.view,
          check_in_date: formData.check_in_date,
          check_out_date: formData.check_out_date,
          ticket_type: formData.ticket_type,
          express_pass: formData.express_pass,
          vip_experience: formData.vip_experience,
          studio_tour: formData.studio_tour,
          behind_scenes_tours: formData.behind_scenes_tours,
          ground_transportation: formData.ground_transportation,
          travel_protection: formData.travel_protection,
          travel_protection_price: formData.travel_protection_price,
          total_price: formData.total_price,
          total_price_due_by: formData.total_price_due_by,
          deposit_due: formData.deposit_due,
          deposit_due_by: formData.deposit_due_by,
          special_requests: formData.special_requests,
          children: formData.children
        }
      };
      console.log('Calling onSave with:', componentData);
      onSave(componentData);
    } else {
      console.log('Form validation failed with errors:', errors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form universal-hollywood-package-form">
      <div className="form-section">
        <h4>Package Details</h4>
        <div className="form-group">
          <label>Package Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Universal Studios Hollywood Package"
            className="form-control"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Hotel Accommodation</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date *</label>
            <input
              type="date"
              name="check_in_date"
              value={formData.check_in_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_in_date ? 'error' : ''}`}
            />
            {errors.check_in_date && <span className="error-text">{errors.check_in_date}</span>}
          </div>
          <div className="form-group">
            <label>Check-out Date *</label>
            <input
              type="date"
              name="check_out_date"
              value={formData.check_out_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_out_date ? 'error' : ''}`}
            />
            {errors.check_out_date && <span className="error-text">{errors.check_out_date}</span>}
          </div>
        </div>
        
        <div className="form-group">
          <label>Hotel Name *</label>
          <select
            name="hotel_name"
            value={formData.hotel_name}
            onChange={handleInputChange}
            className={`form-control ${errors.hotel_name ? 'error' : ''}`}
          >
            <option value="">Select a Hotel</option>
            <optgroup label="Universal CityWalk Hotels">
              <option value="Hilton Los Angeles/Universal City">Hilton Los Angeles/Universal City</option>
              <option value="Sheraton Universal Hotel">Sheraton Universal Hotel</option>
            </optgroup>
            <optgroup label="Nearby Hotels">
              {hotelOptions.slice(2).map(hotel => (
                <option key={hotel} value={hotel}>{hotel}</option>
              ))}
            </optgroup>
            <optgroup label="Other">
              <option value="Other">Other (specify below)</option>
            </optgroup>
          </select>
          {errors.hotel_name && <span className="error-text">{errors.hotel_name}</span>}
        </div>
        
        {formData.hotel_name === 'Other' && (
          <div className="form-group">
            <label>Custom Hotel Name *</label>
            <input
              type="text"
              name="custom_hotel_name"
              value={formData.custom_hotel_name}
              onChange={handleInputChange}
              placeholder="Enter the hotel name"
              className={`form-control ${errors.custom_hotel_name ? 'error' : ''}`}
            />
            {errors.custom_hotel_name && <span className="error-text">{errors.custom_hotel_name}</span>}
          </div>
        )}
        
        <div className="form-row">
          <div className="form-group">
            <label>Room Type</label>
            <input
              type="text"
              name="room_type"
              value={formData.room_type}
              onChange={handleInputChange}
              placeholder="e.g., Standard Room, Suite"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>View</label>
            <input
              type="text"
              name="view"
              value={formData.view}
              onChange={handleInputChange}
              placeholder="e.g., City View, Hills View"
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Theme Park Tickets</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Ticket Type</label>
            <select
              name="ticket_type"
              value={formData.ticket_type}
              onChange={handleInputChange}
              className="form-control"
            >
              {ticketTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Express Pass</label>
            <select
              name="express_pass"
              value={formData.express_pass}
              onChange={handleInputChange}
              className="form-control"
            >
              {expressPassOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Studio Experiences</h4>
        <div className="form-row">
          <div className="form-group">
            <label>VIP Experience</label>
            <select
              name="vip_experience"
              value={formData.vip_experience}
              onChange={handleInputChange}
              className="form-control"
            >
              {vipExperienceOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Studio Tour</label>
            <select
              name="studio_tour"
              value={formData.studio_tour}
              onChange={handleInputChange}
              className="form-control"
            >
              {studioTourOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label>Behind-the-Scenes Tours</label>
          <input
            type="text"
            name="behind_scenes_tours"
            value={formData.behind_scenes_tours}
            onChange={handleInputChange}
            placeholder="e.g., Special Effects Show, Makeup Show"
            className="form-control"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Add-on Services</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Ground Transportation</label>
            <input
              type="text"
              name="ground_transportation"
              value={formData.ground_transportation}
              onChange={handleInputChange}
              placeholder="Transportation details"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Travel Protection</label>
            <select
              name="travel_protection"
              value={formData.travel_protection}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Not Included">Not Included</option>
              <option value="Included">Included</option>
            </select>
          </div>
        </div>
        
        {formData.travel_protection === 'Included' && (
          <div className="form-group">
            <label>Travel Protection Price ($)</label>
            <input
              type="number"
              name="travel_protection_price"
              value={formData.travel_protection_price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className="form-control"
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h4>Pricing & Payment</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Total Price ($) *</label>
            <input
              type="number"
              name="total_price"
              value={formData.total_price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.total_price ? 'error' : ''}`}
            />
            {errors.total_price && <span className="error-text">{errors.total_price}</span>}
          </div>
          <div className="form-group">
            <label>Total Price Due By</label>
            <input
              type="date"
              name="total_price_due_by"
              value={formData.total_price_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Deposit Due ($) *</label>
            <input
              type="number"
              name="deposit_due"
              value={formData.deposit_due}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.deposit_due ? 'error' : ''}`}
            />
            {errors.deposit_due && <span className="error-text">{errors.deposit_due}</span>}
          </div>
          <div className="form-group">
            <label>Deposit Due By</label>
            <input
              type="date"
              name="deposit_due_by"
              value={formData.deposit_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Additional Information</h4>
        <div className="form-group">
          <label>Special Requests</label>
          <textarea
            name="special_requests"
            value={formData.special_requests}
            onChange={handleInputChange}
            rows="3"
            className="form-control"
            placeholder="Any special requests or preferences for this Universal Studios Hollywood package"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Client Options</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({...formData, isOption: e.target.checked})}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({...formData, optionDescription: e.target.value})}
                placeholder="e.g., Optional Universal Hollywood package upgrade or add-on"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for Optional Component</label>
              <select
                value={formData.paymentTiming}
                onChange={(e) => setFormData({...formData, paymentTiming: e.target.value})}
                className="form-control"
              >
                <option value="immediate">Pay Full Price When Selected (e.g., cruise excursions)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay with Final Payment (nothing due initially)</option>
                <option value="bundled">Already Included in Package (no additional payment)</option>
              </select>
              <small className="form-help">
                Choose when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Optional Component Deposit Amount ($)</label>
                <input
                  type="number"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({...formData, optionalDepositAmount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-control"
                />
                <small className="form-help">
                  Amount due when client selects this option (leave blank for full price)
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <AttachmentUpload
        photos={formData.photos || []}
        documents={formData.documents || []}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Add to Quote
        </button>
      </div>
    </form>
  );
};

export default UniversalHollywoodPackageForm;
